import { VerifyCollabInviteTokenDto, VerifyCollabInviteTokenResponseDto, UpdateInfluencerPhoneDto, UpdateInfluencerPhoneResponseDto, CollabInviteTokenErrorDto, CheckBrandAccountDto, CheckBrandAccountResponseDto } from '@foodfluence/shared';
import { apiRequest } from './apiUtils';
import Environment from 'environment';

const API_URL = Environment.API_URL;

export const authApi = {
  /**
   * Check whether an email belongs to a brand (dashboard) account.
   * Used to point brands who land on the creator app to dashboard.foodfluence.io.
   * Fails open to false — this is a redirect hint, not an auth decision.
   */
  checkBrandAccount: async (email: string): Promise<boolean> => {
    try {
      const response = await apiRequest<CheckBrandAccountResponseDto>({
        method: 'POST',
        endpoint: '/auth/check-brand-account',
        data: { email } as CheckBrandAccountDto
      });
      return response.isBrandAccount;
    } catch (error) {
      console.error('Error checking brand account:', error);
      return false;
    }
  },

  /**
   * Verify collab invite token on backend
   * Called when user clicks the invite link
   */
  verifyCollabInviteToken: async (token: string): Promise<VerifyCollabInviteTokenResponseDto> => {
    const response = await fetch(`${API_URL}/auth/verify-collab-invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token } as VerifyCollabInviteTokenDto)
    });

    const data = await response.json();

    // Check if token is invalid (already used or expired) - even if status is 200
    if (data.tokenAlreadyUsed || data.tokenExpired) {
      const error: CollabInviteTokenErrorDto = {
        message: data.message || data.error || 'This invite link is no longer valid',
        error: data.error,
        email: data.email,
        collabId: data.collabId,
        collabStatus: data.collabStatus,
        hasPhoneNumber: data.hasPhoneNumber,
        tokenAlreadyUsed: data.tokenAlreadyUsed,
        tokenExpired: data.tokenExpired
      };
      
      throw error;
    }

    if (!response.ok) {
      // Create typed error object with all relevant fields
      const error: CollabInviteTokenErrorDto = {
        message: data.message || data.error || 'Failed to verify token',
        error: data.error,
        email: data.email,
        collabId: data.collabId,
        collabStatus: data.collabStatus,
        hasPhoneNumber: data.hasPhoneNumber,
        tokenAlreadyUsed: data.tokenAlreadyUsed,
        tokenExpired: data.tokenExpired
      };
      
      throw error;
    }

    return data;
  },

  /**
   * Update phone number for authenticated influencer
   * User must be authenticated
   */
  updateInfluencerPhone: async (
    phoneNumber: string, 
    email: string,
    inviteToken: string,
    collabId: string
  ): Promise<UpdateInfluencerPhoneResponseDto> => {
    return apiRequest({
      method: 'POST',
      endpoint: '/auth/update-influencer-phone',
      data: { 
        phoneNumber, 
        email, 
        inviteToken, 
        collabId 
      } as UpdateInfluencerPhoneDto
    });
  }
};
